import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, requestParams } from '@codegrade/api-core';
import { AllAutoTestResults, AutoTest, AutoTestResult, AutoTestSet, AutoTestSuite, CopyAutoTestData, CreateAutoTestData, CreateOutputHtmlProxyAutoTestData, ExtendedAutoTestResult, ExtendedAutoTestRun, PatchAutoTestData, Proxy, UpdateSetAutoTestData, UpdateSuiteAutoTestData, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function autoTest(config) {
    const copyResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTest)),
    });
    const createResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTest)),
    });
    const addSetResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTestSet)),
    });
    const deleteResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTest)),
    });
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTest)),
    });
    const stopRunResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getRunResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedAutoTestRun)),
    });
    const deleteSetResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const updateSetResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTestSet)),
    });
    const deleteSuiteResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getResultResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedAutoTestResult)),
    });
    const createOutputHtmlProxyResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(Proxy)),
    });
    const getResultBySubmissionResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedAutoTestResult)),
    });
    const getResultsByUserResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(AutoTestResult))),
    });
    const getAttachmentResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parsers.parseArrayBufferResponse),
    });
    const getFixtureResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parsers.parseArrayBufferResponse),
    });
    const getNotStartedResultsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AllAutoTestResults)),
    });
    const showFixtureResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const hideFixtureResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const restartResultResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedAutoTestResult)),
    });
    const startRunResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedAutoTestRun)),
    });
    const updateSuiteResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AutoTestSuite)),
    });
    return Object.freeze({
        /**
         * Copy the given `AutoTest` configuration.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` config which should be copied.
         * @param arg.requestBody The body of the request.
         * @returns The copied `AutoTest` configuration.
         */
        copy: async ({ autoTestId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/copy`, copyResponse.v, JSON.stringify(CopyAutoTestData.encode(requestBody)));
            return result;
        },
        /**
         * Create a new `AutoTest` configuration.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns The newly created `AutoTest`.
         */
        create: async ({ requestBody, }) => {
            const body = coreUtils.toFormData(CreateAutoTestData.encode(requestBody));
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/`, createResponse.v, body);
            return result;
        },
        /**
         * Create a new set within an `AutoTest`
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` wherein you want to create a set.
         * @returns The newly created set.
         */
        addSet: async ({ autoTestId, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/sets/`, addSetResponse.v, undefined);
            return result;
        },
        /**
         * Delete the given `AutoTest`.
         *
         * This route fails if the `AutoTest` has any runs, which should be deleted separately.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The `AutoTest` that should be deleted.
         * @returns Nothing.
         */
        delete: async ({ autoTestId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/auto_tests/${autoTestId}`, deleteResponse.v, undefined);
            return result;
        },
        /**
         * Get the extended version of an `AutoTest` and its runs.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` to get.
         * @returns The extended serialization of an `AutoTest` and the extended serialization of its runs.
         */
        get: async ({ autoTestId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}`, getResponse.v, undefined);
            return result;
        },
        /**
         * Update the settings of an `AutoTest` configuration.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` you want to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated `AutoTest`.
         */
        patch: async ({ autoTestId, requestBody, }) => {
            const body = coreUtils.toFormData(PatchAutoTestData.encode(requestBody));
            const result = await requestParams(config, 'PATCH', `/api/v1/auto_tests/${autoTestId}`, patchResponse.v, body);
            return result;
        },
        /**
         * Delete an `AutoTest` run, this makes it possible to edit the `AutoTest`.
         *
         * This also clears the rubric categories filled in by the `AutoTest`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` of which the run should be deleted.
         * @param arg.runId The id of the run which should be deleted.
         * @returns Nothing.
         */
        stopRun: async ({ autoTestId, runId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/auto_tests/${autoTestId}/runs/${runId}`, stopRunResponse.v, undefined);
            return result;
        },
        /**
         * Get the extended version of an `AutoTestRun`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` which is connected to the requested run.
         * @param arg.runId The id of the run to get.
         * @returns The extended version of an `AutoTestRun`.
         */
        getRun: async ({ autoTestId, runId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/runs/${runId}`, getRunResponse.v, undefined);
            return result;
        },
        /**
         * Delete an `AutoTestSet` (also known as level).
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` of the to be deleted set.
         * @param arg.autoTestSetId The id of the `AutoTestSet` that should be deleted.
         * @returns Nothing.
         */
        deleteSet: async ({ autoTestId, autoTestSetId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/auto_tests/${autoTestId}/sets/${autoTestSetId}`, deleteSetResponse.v, undefined);
            return result;
        },
        /**
         * Update the given `AutoTestSet` (also known as level).
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` of the set that should be updated.
         * @param arg.autoTestSetId The id of the `AutoTestSet` that should be updated.
         * @param arg.requestBody The body of the request.
         * @returns The updated set.
         */
        updateSet: async ({ autoTestId, autoTestSetId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/auto_tests/${autoTestId}/sets/${autoTestSetId}`, updateSetResponse.v, JSON.stringify(UpdateSetAutoTestData.encode(requestBody)));
            return result;
        },
        /**
         * Delete an `AutoTestSuite` (also known as category).
         *
         * @param arg The arguments to the function.
         * @param arg.testId The id of the `AutoTest` where the suite is located in.
         * @param arg.setId The id of the `AutoTestSet` where the suite is located in.
         * @param arg.suiteId The id of the `AutoTestSuite` you want to delete.
         * @returns Nothing.
         */
        deleteSuite: async ({ testId, setId, suiteId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/auto_tests/${testId}/sets/${setId}/suites/${suiteId}`, deleteSuiteResponse.v, undefined);
            return result;
        },
        /**
         * Get the extended version of an `AutoTest` result.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which the result is located.
         * @param arg.runId The id of run in which the result is located.
         * @param arg.resultId The id of the result you want to get.
         * @returns The extended version of a `AutoTestResult`.
         */
        getResult: async ({ autoTestId, runId, resultId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/results/${resultId}`, getResultResponse.v, undefined);
            return result;
        },
        /**
         * Create a proxy to view the files of the given AT result through.
         *
         * This allows you to view files of an AutoTest result (within a suite) without authentication for a limited time.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the AutoTest in which the result is located.
         * @param arg.runId The id of run in which the result is located.
         * @param arg.resultId The id of the result from which you want to get the files.
         * @param arg.suiteId The suite from which you want to proxy the output files.
         * @param arg.requestBody The body of the request.
         * @returns The created proxy.
         */
        createOutputHtmlProxy: async ({ autoTestId, runId, resultId, suiteId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/results/${resultId}/suites/${suiteId}/proxy`, createOutputHtmlProxyResponse.v, JSON.stringify(CreateOutputHtmlProxyAutoTestData.encode(requestBody)));
            return result;
        },
        /**
         * Get the `AutoTest` result for a submission.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which to get the result.
         * @param arg.runId The id of the `AutoTestRun` in which to get the result.
         * @param arg.submissionId The id of the submission from which you want to get the result.
         * @returns The `AutoTest` result for the given data.
         */
        getResultBySubmission: async ({ autoTestId, runId, submissionId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/submissions/${submissionId}/result`, getResultBySubmissionResponse.v, undefined);
            return result;
        },
        /**
         * Get all `AutoTest` results for a given user.
         *
         * If you don't have permission to see the results of the requested user an empty list will be returned.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which to get the results.
         * @param arg.runId The id of the `AutoTestRun` in which to get the results.
         * @param arg.userId The id of the user of which we should get the results.
         * @returns The list of `AutoTest` results for the given user, sorted from oldest to latest.
         */
        getResultsByUser: async ({ autoTestId, runId, userId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/users/${userId}/results/`, getResultsByUserResponse.v, undefined);
            return result;
        },
        /**
         * Get the attachment of an `AutoTest` step.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which the result is located.
         * @param arg.runId The id of run in which the result is located.
         * @param arg.stepResultId The id of the step result of which you want the attachment.
         * @returns The attachment data, as an application/octet-stream.
         */
        getAttachment: async ({ autoTestId, runId, stepResultId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/step_results/${stepResultId}/attachment`, getAttachmentResponse.v, undefined);
            return result;
        },
        /**
         * Get the contents of the given `AutoTestFixture`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The `AutoTest` this fixture is linked to.
         * @param arg.fixtureId The id of the fixture which you want the content.
         * @returns The content of the given fixture.
         */
        getFixture: async ({ autoTestId, fixtureId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_tests/${autoTestId}/fixtures/${fixtureId}`, getFixtureResponse.v, undefined);
            return result;
        },
        /**
         * Get all `AutoTest` results on this instance.
         *
         * @param arg The arguments to the function.
         * @param arg.offset First non started result to get.
         * @param arg.limit Amount of non started results to get.
         * @param arg.state The state the results should be in, currently only `not_started` is supported.
         * @returns The (limited) results of this instance, and the total amount of non requested results.
         */
        getNotStartedResults: async ({ offset = undefined, limit = undefined, state = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/auto_test_results/`, getNotStartedResultsResponse.v, undefined, {
                offset,
                limit,
                state,
            });
            return result;
        },
        /**
         * Change the visibility of the given fixture.
         *
         * Doing a `POST` request to this route will hide the fixture, doing a `DELETE` request to this route will set `hidden` to `False`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The `AutoTest` this fixture is linked to.
         * @param arg.fixtureId The fixture which you to hide or show.
         * @returns Nothing.
         */
        showFixture: async ({ autoTestId, fixtureId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/auto_tests/${autoTestId}/fixtures/${fixtureId}/hide`, showFixtureResponse.v, undefined);
            return result;
        },
        /**
         * Change the visibility of the given fixture.
         *
         * Doing a `POST` request to this route will hide the fixture, doing a `DELETE` request to this route will set `hidden` to `False`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The `AutoTest` this fixture is linked to.
         * @param arg.fixtureId The fixture which you to hide or show.
         * @returns Nothing.
         */
        hideFixture: async ({ autoTestId, fixtureId, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/fixtures/${fixtureId}/hide`, hideFixtureResponse.v, undefined);
            return result;
        },
        /**
         * Restart an `AutoTest` result.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which the result is located.
         * @param arg.runId The id of run in which the result is located.
         * @param arg.resultId The id of the result you want to restart.
         * @returns The extended version of a `AutoTestResult`.
         */
        restartResult: async ({ autoTestId, runId, resultId, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/runs/${runId}/results/${resultId}/restart`, restartResultResponse.v, undefined);
            return result;
        },
        /**
         * Start a run for the given `AutoTest`.
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` for which you want to start a run.
         * @returns The started run or a empty mapping if you do not have permission to see `AutoTest` runs.
         */
        startRun: async ({ autoTestId, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/auto_tests/${autoTestId}/runs/`, startRunResponse.v, undefined);
            return result;
        },
        /**
         * Update or create a `AutoTestSuite` (also known as category)
         *
         * @param arg The arguments to the function.
         * @param arg.autoTestId The id of the `AutoTest` in which this suite should be created.
         * @param arg.setId The id the `AutoTestSet` in which this suite should be created.
         * @param arg.requestBody The body of the request.
         * @returns The just updated or created `AutoTestSuite`.
         */
        updateSuite: async ({ autoTestId, setId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/auto_tests/${autoTestId}/sets/${setId}/suites/`, updateSuiteResponse.v, JSON.stringify(UpdateSuiteAutoTestData.encode(requestBody)));
            return result;
        },
    });
}
